import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, t, toggleLanguage } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-[#2A2520]/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <motion.button
            onClick={() => scrollToSection('hero')}
            className="text-2xl font-serif text-[#F5F1E8] hover:text-[#D4AF37] transition-colors duration-300"
            whileHover={{ scale: 1.05 }}
          >
            Ca' del Sole Italia
          </motion.button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {[
              { label: t.nav.home, id: 'hero' },
              { label: t.nav.casa, id: 'casa' },
              { label: t.nav.eventi, id: 'eventi' },
              { label: t.nav.galleria, id: 'galleria' },
              { label: t.nav.location, id: 'location' },
              { label: t.nav.contatti, id: 'contatti' },
            ].map((item, index) => (
              <motion.button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-[#F5F1E8] hover:text-[#D4AF37] transition-colors duration-300 text-sm tracking-wider uppercase"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -2 }}
              >
                {item.label}
              </motion.button>
            ))}
          </nav>

          {/* Language Switcher */}
          <div className="hidden md:flex items-center gap-2">
            <Globe className="w-4 h-4 text-[#D4AF37]" />
            {['it', 'en', 'de', 'fr', 'es'].map((lang, idx) => (
              <React.Fragment key={lang}>
                <button
                  onClick={() => toggleLanguage(lang)}
                  className={`text-sm transition-colors duration-300 ${
                    language === lang ? 'text-[#D4AF37]' : 'text-[#F5F1E8] hover:text-[#D4AF37]'
                  }`}
                  data-testid={`lang-${lang}-btn`}
                >
                  {lang.toUpperCase()}
                </button>
                {idx < 4 && <span className="text-[#F5F1E8]/50">|</span>}
              </React.Fragment>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-[#F5F1E8]"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden pt-4 pb-2 space-y-4"
          >
            {[
              { label: t.nav.home, id: 'hero' },
              { label: t.nav.casa, id: 'casa' },
              { label: t.nav.eventi, id: 'eventi' },
              { label: t.nav.galleria, id: 'galleria' },
              { label: t.nav.location, id: 'location' },
              { label: t.nav.contatti, id: 'contatti' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="block w-full text-left text-[#F5F1E8] hover:text-[#D4AF37] transition-colors duration-300 py-2"
              >
                {item.label}
              </button>
            ))}
            <div className="flex items-center gap-2 pt-2 border-t border-[#F5F1E8]/20 flex-wrap">
              <Globe className="w-4 h-4 text-[#D4AF37]" />
              {['it', 'en', 'de', 'fr', 'es'].map((lang, idx) => (
                <React.Fragment key={lang}>
                  <button
                    onClick={() => toggleLanguage(lang)}
                    className={`text-sm transition-colors duration-300 ${
                      language === lang ? 'text-[#D4AF37]' : 'text-[#F5F1E8]'
                    }`}
                    data-testid={`mobile-lang-${lang}-btn`}
                  >
                    {lang.toUpperCase()}
                  </button>
                  {idx < 4 && <span className="text-[#F5F1E8]/50">|</span>}
                </React.Fragment>
              ))}
            </div>
          </motion.nav>
        )}
      </div>
    </motion.header>
  );
};

export default Header;
